import React, { useContext, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { AppContext } from "../../../../context/AppContext";
import RegisterModal from "../../../Common/Modals/RegisterModal";
import CashierModal from "../../../Modals/CashierModals/CashierModal";
import Modal from "../../../Modals/Modal";
import SearchModal from "../../../Modals/SearchModal/SearchModal";
import TotalMoneyContainer from "../TotalMoneyContainer";
import { StyledNavBar } from "./styles";
import { useAuth } from "@/context/AuthContext";
import useMemberNotifications from "@/hooks/useMemberNotifications";
import useSiteSettings, { resolveSiteLogo } from "@/hooks/useSiteSettings";

const formatBalance = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0";
  return number.toLocaleString("ko-KR", { maximumFractionDigits: 0 });
};

const readNumberByPaths = (source, paths) => {
  for (const path of paths) {
    const value = path.split(".").reduce((current, key) => current?.[key], source);
    if (value !== undefined && value !== null && value !== "") {
      const number = Number(value);
      if (Number.isFinite(number)) return number;
    }
  }
  return 0;
};

const UserIcon = () => (
  <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
    <path d="M20 21a8 8 0 0 0-16 0" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const BellIcon = () => (
  <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
);

const MemberNoticeModal = ({ notice, onClose, onOpen }) => {
  return (
    <Modal
      isOpen={Boolean(notice)}
      onClose={onClose}
      maxWidth={420}
      zIndex={9000}
      panelClassName="member-notice-dialog-panel"
    >
      {notice ? (
        <div className="member-notice-content">
          <button
            type="button"
            className="member-notice-close"
            aria-label="알림 닫기"
            onClick={onClose}
          >
            ×
          </button>
          <span className="member-notice-label">알림</span>
          <strong>{notice.title}</strong>
          <p>
            {notice.message}
            {notice.count > 1 ? ` (${notice.count}건)` : ""}
          </p>
          <div className="member-notice-actions">
            <button type="button" className="notice-secondary" onClick={onClose}>
              닫기
            </button>
            <button type="button" className="notice-primary" onClick={onOpen}>
              확인하기
            </button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

const NavBar = () => {
  const { isTabletScreen, isSidebarOpen, closeCashierModal } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAuthenticated = Boolean(user?.isAuthenticated);
  const isBootstrappingAuth = user?.isLoading === true;
  const suppressedMemberNoticeTypes = useMemo(
    () => ({
      note: location.pathname.startsWith("/notice-board"),
      inquiry: location.pathname.startsWith("/customer-service-center"),
    }),
    [location.pathname]
  );
  const { alerts, dismissAlert, noteUnreadCount } = useMemberNotifications(isAuthenticated, {
    suppressedTypes: suppressedMemberNoticeTypes,
  });
  const { settings, isLoading: isSettingsLoading } = useSiteSettings();
  const backendLogo = resolveSiteLogo(settings);
  const activeNotice = alerts[0];

  const balance = useMemo(() => formatBalance(user?.balance), [user?.balance]);
  const walletItems = useMemo(() => {
    const source = user?.profile || {};
    return [
      {
        key: "point",
        label: "포인트",
        unit: "P",
        value: readNumberByPaths(source, [
          "wallet.point_balance",
          "point_balance",
          "points",
          "point",
        ]),
      },
      {
        key: "comp",
        label: "콤프",
        unit: "P",
        value: readNumberByPaths(source, [
          "wallet.rolling_balance",
          "wallet.comp_balance",
          "rolling_balance",
          "comp_balance",
          "comp",
        ]),
      },
      {
        key: "holdem-money",
        label: "홀덤머니",
        unit: "원",
        value: readNumberByPaths(source, [
          "wallet.holdem_balance",
          "wallet.holdem_money",
          "holdem_balance",
          "holdem_money",
        ]),
      },
      {
        key: "holdem-comp",
        label: "홀덤콤프",
        unit: "P",
        value: readNumberByPaths(source, [
          "wallet.holdem_rolling_balance",
          "wallet.holdem_comp_balance",
          "wallet.holdem_comp",
          "holdem_rolling_balance",
          "holdem_comp_balance",
          "holdem_comp",
        ]),
      },
    ];
  }, [user?.profile]);
  const notificationLabel =
    noteUnreadCount > 0 ? `쪽지 알림 ${noteUnreadCount}건` : "쪽지 알림";

  const closeActiveNotice = () => {
    if (activeNotice) dismissAlert(activeNotice.id);
  };

  const openActiveNotice = () => {
    if (!activeNotice) return;
    dismissAlert(activeNotice.id);
    closeCashierModal?.();
    navigate(activeNotice.to);
  };

  return (
    <StyledNavBar $isMobileSidebarOpen={isTabletScreen && isSidebarOpen}>
      <header className="top-nav">
        <div className={`nav-left ${isTabletScreen ? "is-tablet" : ""}`}>
          <Link to="/" className="brand-link" aria-label="홈">
            {backendLogo ? (
              <img src={backendLogo} alt="logo" className="brand-logo backend-logo" />
            ) : isSettingsLoading ? (
              <span className="brand-logo-skeleton" aria-hidden="true" />
            ) : null}
          </Link>
        </div>

        {isAuthenticated ? (
          <div className="nav-center-wallet">
            <TotalMoneyContainer money={balance} walletItems={walletItems} />
            <CashierModal button="지갑" triggerClassName="wallet-button" />
          </div>
        ) : null}

        <div className="nav-right">
          {!isAuthenticated && !isBootstrappingAuth ? (
            <div className="nav-public-actions">
              <SearchModal triggerClassName="header-search-button" triggerAriaLabel="검색" />
              <div className="nav-auth-actions">
                <RegisterModal modalOption="login" buttonText="로그인" />
                <RegisterModal modalOption="register" buttonText="회원가입" />
                <RegisterModal modalOption="anonymous" buttonText="무기명 가입" />
              </div>
            </div>
          ) : null}

          {isAuthenticated ? (
            <div className="nav-member-actions">
              <SearchModal triggerClassName="nav-icon-button member-search-button" triggerAriaLabel="검색" />
              <Link to="/account/profile" className="nav-icon-button" aria-label="마이페이지">
                <UserIcon />
              </Link>
              <Link to="/notice-board" className="nav-icon-button has-badge" aria-label={notificationLabel}>
                <BellIcon />
                {noteUnreadCount > 0 ? (
                  <span className="nav-badge">{noteUnreadCount > 99 ? "99+" : noteUnreadCount}</span>
                ) : null}
              </Link>
            </div>
          ) : null}
        </div>
      </header>
      <MemberNoticeModal
        notice={activeNotice}
        onClose={closeActiveNotice}
        onOpen={openActiveNotice}
      />
    </StyledNavBar>
  );
};

export default NavBar;
